import { tss } from 'tss-react/mui';

import { Button, IconButton, Skeleton, Tooltip, alpha } from '@mui/material';

import {
  AddOutlined,
  ChevronRightOutlined,
  SearchOutlined,
} from '@mui/icons-material';

import type { Dispatch, SetStateAction } from 'react';
import { useState, useRef } from 'react';

import { graphql, type DocumentType } from '@/gql';

import type { OwnerPermisson } from '@/modules/files/hooks/useFilePermissions';

import type { FileManagerUploadingState } from '@/modules/files/components/file-manager/FileManagerUploadingBar';

import { isRestrictedSegment } from './toolbarUtils';
import {
  FileManagerBreadcrumbsBarSegment,
  type Segment,
} from './breadcrumbs-bar/FileManagerBreadcrumbsBarSegment';
import { FileManagerFolderMenu } from './breadcrumbs-bar/FileManagerFolderMenu';

const FILE_MANAGER_BREADCRUMBS_BAR_FILES_FOR_OWNER_FRAGMENT = graphql(`
  fragment fileManagerBreadcrumbsBarFilesForOwnerFields on FilesForOwner {
    ...fileManagerFolderMenuFilesForOwnerFields
  }
`);

interface Props {
  segments: Segment[];
  ownerPermissions: OwnerPermisson[];
  filesForOwner:
    | DocumentType<typeof FILE_MANAGER_BREADCRUMBS_BAR_FILES_FOR_OWNER_FRAGMENT>
    | null
    | undefined;
  setSearchMode: Dispatch<SetStateAction<boolean>>;
  setUploadingState: Dispatch<SetStateAction<FileManagerUploadingState>>;
}

const useStyles = tss.create(({ theme }) => ({
  root: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    minHeight: theme.spacing(8),
  },

  breadcrumbs: {
    display: 'flex',
    alignItems: 'stretch',
    minWidth: 0,
  },

  restricted: {
    backgroundColor: alpha(theme.palette.error.main, 0.1),
  },

  segmentWrapper: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',

    '&:first-child': {
      marginLeft: theme.spacing(-1),
    },
  },

  separator: {
    color: theme.hint,
  },

  buttons: {
    display: 'flex',
    alignItems: 'center',
  },

  leftIcon: {
    marginRight: theme.spacing(1),
  },

  actionButton: {
    color: theme.palette.action.active,
  },
}));

export const FileManagerBreadcrumbsBar = ({
  segments,
  ownerPermissions,
  filesForOwner,
  setSearchMode,
  setUploadingState,
}: Props) => {
  const [folderMenuOpen, setFolderMenuOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);
  const addMenuRef = useRef<HTMLDivElement>(null);
  const lastSegmentRef = useRef<HTMLDivElement>(null);

  const { classes, cx } = useStyles();

  const owner = filesForOwner?.owner ?? null;

  const lastSegment = segments[segments.length - 1];
  const lastSegmentPermission = ownerPermissions.find(
    (perm) => perm._id === lastSegment.ownerId,
  );
  const canEditLastSegment = Boolean(lastSegmentPermission?.userCanEditFiles);

  return (
    <div className={classes.root}>
      <div className={classes.breadcrumbs}>
        {filesForOwner ? (
          segments.map((segment, idx) => {
            const isLast = idx + 1 === segments.length;
            return (
              <div
                key={segment._id || undefined}
                ref={isLast ? lastSegmentRef : undefined}
                className={cx({
                  [classes.restricted]: isRestrictedSegment(
                    segment,
                    owner?._id ?? '',
                    owner?.__typename,
                  ),
                  [classes.segmentWrapper]: true,
                })}
              >
                <FileManagerBreadcrumbsBarSegment
                  segment={segment}
                  ownerPermissions={ownerPermissions}
                  isLast={isLast}
                  openFolderMenu={() => setFolderMenuOpen(true)}
                />
                {isLast ? (
                  Boolean(segment.folder) &&
                  filesForOwner &&
                  owner?.__typename && (
                    <FileManagerFolderMenu
                      folder={segment.file ?? null}
                      filesForOwner={filesForOwner}
                      rootOwnerId={owner?._id ?? ''}
                      rootOwnerType={owner?.__typename}
                      segment={segment}
                      canEdit={canEditLastSegment}
                      anchorEl={folderMenuOpen ? lastSegmentRef.current : null}
                      setUploadingState={setUploadingState}
                      open={folderMenuOpen}
                      onClose={() => setFolderMenuOpen(false)}
                    />
                  )
                ) : (
                  <ChevronRightOutlined className={classes.separator} />
                )}
              </div>
            );
          })
        ) : (
          <Skeleton
            sx={{ alignSelf: 'center', justifySelf: 'center' }}
            variant="text"
            width={200}
            height={40}
          />
        )}
      </div>
      <div className={classes.buttons}>
        {canEditLastSegment && lastSegment.folder && (
          <div ref={addMenuRef}>
            <Button
              data-test="file-manager-toolbar-add-button"
              className={classes.actionButton}
              onClick={() => setAddMenuOpen(true)}
            >
              <AddOutlined className={classes.leftIcon} />
              Add
            </Button>
            {filesForOwner && owner?.__typename && (
              <FileManagerFolderMenu
                folder={lastSegment.file ?? null}
                filesForOwner={filesForOwner}
                rootOwnerId={owner?._id ?? ''}
                rootOwnerType={owner?.__typename}
                segment={lastSegment}
                canEdit={canEditLastSegment}
                anchorEl={addMenuOpen ? addMenuRef.current : null}
                setUploadingState={setUploadingState}
                open={addMenuOpen}
                onClose={() => setAddMenuOpen(false)}
                createOnly
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              />
            )}
          </div>
        )}
        <Tooltip title="Search files">
          <IconButton onClick={() => setSearchMode(true)} size="large">
            <SearchOutlined />
          </IconButton>
        </Tooltip>
      </div>
    </div>
  );
};
